import { stringArrayParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

const MIME_TO_FORMAT: Record<string, string> = {
  'image/jpeg': 'jpeg',
  'image/jpg': 'jpeg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/gif': 'gif',
  'image/svg+xml': 'svg',
  'text/html': 'html',
};

const formatOf = (mimeType: string | undefined): string => {
  if (!mimeType) return 'unknown';
  const lower = mimeType.toLowerCase();
  return MIME_TO_FORMAT[lower] ?? lower.split('/')[1] ?? 'unknown';
};

/**
 * File format check.
 *
 * Uses the MIME type sniffed from magic bytes; a mismatch with the type the
 * client claimed is reported in `details` but never changes the verdict by
 * itself, because the sniffed value is the authoritative one.
 */
export const fileFormatCheck: ValidationCheck = {
  id: 'file_format',
  title: 'Тип файла',
  description: 'Проверяет контейнер изображения по сигнатуре файла, а не по расширению.',
  kind: 'technical',
  requires: ['fileInfo'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['fileInfo']);
    if (missing) return notChecked(ctx, fileFormatCheck, missing, ctx.rule.severity);

    const allowed = stringArrayParam(ctx.rule.params, 'formats', ['jpeg']).map((item) =>
      item.toLowerCase(),
    );
    const detected = ctx.fileInfo.detectedMimeType ?? ctx.image.mimeType;
    const format = formatOf(detected);

    if (format === 'unknown') {
      return plainResult({
        id: fileFormatCheck.id,
        title: fileFormatCheck.title,
        status: 'not_checked',
        severity: ctx.rule.severity,
        message: 'Не удалось определить формат файла по сигнатуре.',
        fixability: 'fixable',
        details: { detectedMimeType: detected ?? null, allowed },
      });
    }

    const ok = allowed.includes(format);
    const mismatch =
      ctx.fileInfo.declaredMimeType !== undefined &&
      ctx.fileInfo.declaredMimeType.toLowerCase() !== (detected ?? '').toLowerCase();

    return plainResult({
      id: fileFormatCheck.id,
      title: fileFormatCheck.title,
      status: ok ? 'pass' : 'fail',
      severity: ctx.rule.severity,
      value: allowed.indexOf(format) >= 0 ? allowed.indexOf(format) : undefined,
      message: ok
        ? `Формат ${format.toUpperCase()} разрешён для этого документа.`
        : `Формат ${format.toUpperCase()} не принимается: требуется ${allowed
            .map((item) => item.toUpperCase())
            .join(' или ')}.`,
      requirement: `Допустимые форматы: ${allowed.map((item) => item.toUpperCase()).join(', ')}.`,
      fixability: 'fixable',
      confidence: 1,
      details: {
        format,
        detectedMimeType: detected ?? null,
        declaredMimeType: ctx.fileInfo.declaredMimeType ?? null,
        declaredMismatch: mismatch,
        allowed,
      },
    });
  },
};
